import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Settings } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import DarkModeToggle from "../../components/DarkModeToggle";
import { useReportById } from "../../hooks/useReportHistory";
import { useTrackPageView } from "../../hooks/useTrackPageView";
import { trackEvent } from "../../utils/mixpanel";
import { PERSONAS } from "../../utils/personas";
import { useRebuildReportAsync } from "@/src/hooks/useRebuildReportAsync";

const planToStr = (plan) => {
  if (!plan || !Array.isArray(plan)) return "";
  return plan
    .map((step) => {
      let s = step.request || step.title || "";
      if (step.slides && Array.isArray(step.slides) && step.slides.length > 0) {
        s +=
          "\n\n" +
          step.slides.map((slide) => ` - ${slide.sub_request || slide.title || ""}`).join("\n\n");
      }
      return s;
    })
    .join("\n");
};

const getDefaultPrompt = (report) => {
  return report?.request + "\n\nUse the following plan:\n\n" + planToStr(report?.plan);
};

function ReportEdit() {
  const { id } = useParams();
  useTrackPageView("Edit Plan", { report_id: id });
  const navigate = useNavigate();
  const { data: report, isLoading } = useReportById(id);
  const reportPersona = report?.persona;
  const rebuildReport = useRebuildReportAsync();
  const defaultPrompt = getDefaultPrompt(report);
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const nextDefaultPersona = formData.get("defaultPersona");
    const nextDefaultPrompt = formData.get("defaultPrompt");
    const nextPrompt = formData.get("promptWithPlan");
    const nextPersona = formData.get("persona");
    const hasChanged = nextDefaultPrompt !== nextPrompt || nextDefaultPersona !== nextPersona;
    if (hasChanged) {
      try {
        trackEvent("Report Regenerated", {
          report_id: report.id,
          new_persona: nextPersona,
          old_persona: nextDefaultPersona,
          persona_changed: nextDefaultPersona !== nextPersona,
          prompt_changed: nextDefaultPrompt !== nextPrompt,
        });
        const nextReport = await rebuildReport.mutateAsync({
          id: report.id,
          request: nextPrompt,
          persona: nextPersona,
        });
        navigate(`/v1/report/${nextReport.id}`);
        return;
      } catch (error) {
        console.error("Failed to rebuild report:", error);
        navigate(`/v1/report/${id}`);
        return;
      }
    }
    navigate(`/v1/report/${id}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-8 max-w-4xl">
          <div className="text-center text-muted-foreground">Loading report...</div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-8 max-w-4xl">
          <Alert>
            <AlertDescription>
              Report not found.
              <Button asChild className="ml-2">
                <Link to="/v1/reports">View Reports</Link>
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Settings and Dark mode toggle - top left */}
      <div className="absolute top-0 left-0 p-6 flex items-center gap-2">
        <Link to="/v1/settings">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
          >
            <Settings className="h-4 w-4" />
            <span className="sr-only">Settings</span>
          </Button>
        </Link>
        <div className="h-5 w-px bg-border" />
        <DarkModeToggle />
      </div>

      <div className="container mx-auto p-8 max-w-4xl pt-20">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/v1/report/${id}`)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Report
          </Button>
          <h1 className="text-2xl font-bold">Edit Report Configuration</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <input type="hidden" name="defaultPrompt" value={defaultPrompt} />
          <input type="hidden" name="defaultPersona" value={report?.persona} />

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="prompt">Report Prompt</Label>
              <Textarea
                id="prompt"
                defaultValue={defaultPrompt}
                name="promptWithPlan"
                placeholder="What would you like to research?"
                className="min-h-[120px] resize-vertical"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="style">Report Style</Label>
              <Select defaultValue={reportPersona} name="persona">
                <SelectTrigger id="style">
                  <SelectValue placeholder="Select style" />
                </SelectTrigger>
                <SelectContent>
                  {PERSONAS.map((persona) => (
                    <SelectItem key={persona.value} value={persona.value}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{persona.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => navigate(`/v1/report/${id}`)}>
                Cancel
              </Button>
              <Button type="submit" disabled={rebuildReport.isPending}>
                {rebuildReport.isPending ? "Building..." : "Save & Rebuild"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReportEdit;
